/**
 * hali-mega-menu.js
 * Custom Element für das globale Desktop Mega-Menu.
 *
 * Architektur:
 *   - .mega-menu__section[data-section="ebene1-handle"] — eine pro Ebene-1-Link
 *   - Tabs innerhalb = Ebene-2-Items mit Kindern
 *   - Panel-Links = Ebene-3-Items
 *
 * Verbindung zu Nav-Links:
 *   _header-menu.liquid setzt data-mega-menu-target="[handle]" auf Ebene-1-Links mit Kindern.
 *   Event-Delegation via einem document click-Listener.
 *
 * Panel-State:
 *   data-open="true/false" steuert visibility/opacity via CSS.
 *   Aktive Section: hidden-Attribut entfernt.
 *   Aktiver Tab: aria-selected="true", tabindex="0".
 *   Aktives Panel: hidden-Attribut entfernt.
 */
class HaliMegaMenu extends HTMLElement {
  /** Handle der aktiven Ebene-1-Section (null = Panel geschlossen) */
  #activeSection = null;

  /** Handle des aktiven Ebene-2-Tabs innerhalb der Section */
  #activeHandle = null;

  /** Letzter Nav-Link-Trigger (für Fokus-Rückgabe nach Escape) */
  #lastTrigger = null;

  /** Gebundene Event-Handler für removeEventListener */
  #boundDocClick = null;
  #boundNavKeydown = null;
  #boundKeydown = null;
  #boundFocusOut = null;
  #boundTabClick = null;
  #boundInternalMousedown = null;

  /** Zeitstempel des letzten Mousedown innerhalb des Panels (für focusout-Guard) */
  #lastInternalMousedown = 0;

  /** ResizeObserver für Scroll-Button */
  #resizeObserver = null;

  connectedCallback() {
    this.#boundDocClick = this.#onDocClick.bind(this);
    document.addEventListener('click', this.#boundDocClick);

    this.#boundNavKeydown = this.#onNavKeydown.bind(this);
    document.addEventListener('keydown', this.#boundNavKeydown);

    this.#boundKeydown = this.#onKeydown.bind(this);
    this.#boundFocusOut = this.#onFocusOut.bind(this);
    this.#boundTabClick = this.#onTabClick.bind(this);
    this.#boundInternalMousedown = () => { this.#lastInternalMousedown = Date.now(); };
    this.addEventListener('keydown', this.#boundKeydown);
    this.addEventListener('focusout', this.#boundFocusOut);
    this.addEventListener('click', this.#boundTabClick);
    this.addEventListener('mousedown', this.#boundInternalMousedown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#boundDocClick);
    document.removeEventListener('keydown', this.#boundNavKeydown);
    this.removeEventListener('keydown', this.#boundKeydown);
    this.removeEventListener('focusout', this.#boundFocusOut);
    this.removeEventListener('click', this.#boundTabClick);
    this.removeEventListener('mousedown', this.#boundInternalMousedown);
    this.#resizeObserver?.disconnect();
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  /** Panel öffnen und Ebene-1-Section aktivieren. */
  open(sectionHandle) {
    this.dataset.open = 'true';
    this.#activateSection(sectionHandle);
    this.#setTriggerExpanded(sectionHandle, true);
  }

  /** Panel schließen. */
  close() {
    this.dataset.open = 'false';
    this.#setTriggerExpanded(this.#activeSection, false);
    // Aktive Section im DOM verstecken, damit beim nächsten open() kein
    // Zustands-Leak aus dem vorigen Besuch entsteht.
    if (this.#activeSection) {
      this.querySelector(`[data-section="${this.#activeSection}"]`)
        ?.setAttribute('hidden', '');
    }
    this.#activeSection = null;
    this.#activeHandle = null;
  }

  /**
   * Ebene-2-Tab aktivieren innerhalb der aktuellen Section.
   * Setzt aria-selected + tabindex auf Tab, entfernt hidden vom Panel.
   */
  activateTab(handle) {
    const section = this.#activeSection
      ? this.querySelector(`[data-section="${this.#activeSection}"]`)
      : null;
    if (!section) return;

    // Bisherigen Tab deaktivieren
    if (this.#activeHandle) {
      const prevTab = section.querySelector(`[data-handle="${this.#activeHandle}"][role="tab"]`);
      const prevPanel = section.querySelector(
        `#mega-panel-${this.#activeSection}-${this.#activeHandle}`
      );
      prevTab?.setAttribute('aria-selected', 'false');
      prevTab?.setAttribute('tabindex', '-1');
      prevPanel?.setAttribute('hidden', '');
    }

    // Neuen Tab aktivieren
    const tab = section.querySelector(`[data-handle="${handle}"][role="tab"]`);
    const panel = section.querySelector(
      `#mega-panel-${this.#activeSection}-${handle}`
    );

    tab?.setAttribute('aria-selected', 'true');
    tab?.setAttribute('tabindex', '0');
    panel?.removeAttribute('hidden');

    this.#activeHandle = handle;
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  /**
   * Ebene-1-Section wechseln: bisherige verstecken, neue einblenden,
   * ersten Tab aktivieren und fokussieren.
   */
  #activateSection(handle) {
    // Bisherige Section verstecken
    if (this.#activeSection && this.#activeSection !== handle) {
      this.querySelector(`[data-section="${this.#activeSection}"]`)?.setAttribute('hidden', '');
      this.#activeHandle = null;
    }

    const section = this.querySelector(`[data-section="${handle}"]`);
    if (!section) return;

    section.removeAttribute('hidden');
    this.#activeSection = handle;
    this.#activeHandle = null;

    // Alle Panel- und Tab-Zustände innerhalb dieser Section zurücksetzen,
    // damit kein Zustand aus einem vorigen Besuch durchscheint.
    section.querySelectorAll('[role="tabpanel"]').forEach(p => p.setAttribute('hidden', ''));
    section.querySelectorAll('[role="tab"]').forEach(t => {
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });

    // Ersten Tab aktivieren und Scroll-Button initialisieren
    const firstTab = section.querySelector('[role="tab"]');
    if (firstTab) {
      // Tab ohne Kinder (data-href) ist ein Direkt-Link — nicht als aktiv markieren.
      if (!firstTab.dataset.href) {
        this.activateTab(firstTab.dataset.handle);
      }
      firstTab.focus();
      this.#initScrollButton(section);
    } else {
      // Fallback (kein Tab): ersten Link fokussieren
      section.querySelector('a[href]')?.focus();
    }
  }

  #onDocClick(event) {
    const trigger = event.target.closest('[data-mega-menu-target]');

    if (trigger) {
      event.preventDefault();
      const handle = trigger.dataset.megaMenuTarget;
      this.#lastTrigger = trigger;

      if (this.dataset.open === 'true') {
        if (this.#activeSection === handle) {
          // Gleicher Trigger → schließen
          this.close();
          trigger.focus();
        } else {
          // Anderer Trigger → Section wechseln
          this.#setTriggerExpanded(this.#activeSection, false);
          this.#activateSection(handle);
          this.#setTriggerExpanded(handle, true);
        }
      } else {
        this.open(handle);
      }
      return;
    }

    // Outside-Click
    if (this.dataset.open === 'true' && !this.contains(event.target)) {
      this.close();
    }
  }

  /** Space auf <a>-Nav-Triggern (feuern keinen click bei Space). */
  #onNavKeydown(event) {
    if (event.key !== ' ') return;
    const trigger = event.target.closest('[data-mega-menu-target]');
    if (!trigger) return;
    event.preventDefault();
    trigger.click();
  }

  #onFocusOut(event) {
    if (this.dataset.open !== 'true') return;
    // Wenn Fokus innerhalb des Panels bleibt (z.B. Tab → Tab), sofort ignorieren.
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    setTimeout(() => {
      // Guard für Browser die Buttons beim Click nicht fokussieren (z.B. Safari):
      // Mousedown innerhalb des Panels < 200ms her → kein Close.
      if (Date.now() - this.#lastInternalMousedown < 200) return;
      const active = document.activeElement;
      const insidePanel = this.contains(active);
      const onTrigger = Boolean(active?.closest('[data-mega-menu-target]'));
      if (!insidePanel && !onTrigger) this.close();
    }, 100);
  }

  /** Klick auf Ebene-2-Tab → Panel wechseln oder bei Tabs ohne Kinder direkt navigieren. */
  #onTabClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    if (tab.dataset.href) {
      window.location.href = tab.dataset.href;
      return;
    }
    this.activateTab(tab.dataset.handle);
  }

  #onKeydown(event) {
    // Escape: Panel schließen
    if (event.key === 'Escape') {
      if (this.dataset.open !== 'true') return;
      this.close();
      this.#lastTrigger?.focus();
      return;
    }

    const section = this.#activeSection
      ? this.querySelector(`[data-section="${this.#activeSection}"]`)
      : null;
    if (!section) return;

    // Arrow-Keys / Home / End auf Tab-Leiste
    const tab = event.target.closest('[role="tab"]');
    if (tab && section.contains(tab)) {
      const tabs = [...section.querySelectorAll('[role="tab"]')];
      const currentIndex = tabs.indexOf(tab);
      let targetIndex = null;

      if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') targetIndex = 0;
      else if (event.key === 'End') targetIndex = tabs.length - 1;

      if (targetIndex !== null) {
        event.preventDefault();
        this.activateTab(tabs[targetIndex].dataset.handle);
        tabs[targetIndex].focus();
        return;
      }

      // Tab → erstes fokussierbares Element im Panel
      if (event.key === 'Tab' && !event.shiftKey) {
        const panel = section.querySelector(
          `#mega-panel-${this.#activeSection}-${this.#activeHandle}`
        );
        const focusable = panel?.querySelectorAll('a[href], button:not([disabled])');
        if (focusable?.length > 0) {
          event.preventDefault();
          focusable[0].focus();
        }
        return;
      }
    }

    // Shift+Tab auf erstem Panel-Element → zurück zum aktiven Tab
    const panel = event.target.closest('[role="tabpanel"]');
    if (panel && section.contains(panel) && event.key === 'Tab' && event.shiftKey) {
      const focusable = panel.querySelectorAll('a[href], button:not([disabled])');
      if (focusable.length > 0 && event.target === focusable[0]) {
        event.preventDefault();
        section.querySelector('[role="tab"][aria-selected="true"]')?.focus();
      }
    }
  }

  #setTriggerExpanded(handle, expanded) {
    if (!handle) return;
    document
      .querySelector(`[data-mega-menu-target="${handle}"]`)
      ?.setAttribute('aria-expanded', String(expanded));
  }

  /** Scroll-Button für eine Section initialisieren. */
  #initScrollButton(section) {
    const btnRight = section.querySelector('[data-scroll-btn-right]');
    const btnLeft = section.querySelector('[data-scroll-btn-left]');
    const tabsContainer = section.querySelector('.mega-menu__tabs');
    if (!tabsContainer) return;

    // Bestehenden Observer trennen
    this.#resizeObserver?.disconnect();

    btnRight?.addEventListener('click', () =>
      tabsContainer.scrollBy({ left: 200, behavior: 'smooth' })
    );
    btnLeft?.addEventListener('click', () =>
      tabsContainer.scrollBy({ left: -200, behavior: 'smooth' })
    );

    const update = () => {
      const hasOverflow = tabsContainer.scrollWidth > tabsContainer.clientWidth;
      const atEnd =
        tabsContainer.scrollLeft + tabsContainer.clientWidth >= tabsContainer.scrollWidth - 4;
      const atStart = tabsContainer.scrollLeft <= 4;

      const showRight = hasOverflow && !atEnd;
      const showLeft = hasOverflow && !atStart;

      // Fokus retten: wenn ein Scroll-Button verschwindet während er fokussiert ist
      // (z.B. Smooth-Scroll erreicht Ende), Fokus verschieben — sonst fällt er auf
      // <body> und focusout schließt das Panel. Ziel: der gegenüberliegende, noch
      // sichtbare Scroll-Button (liegt direkt daneben, beim Erreichen eines Endes
      // immer sichtbar). Fallback: aktiver bzw. erster Tab — wichtig, weil Sections
      // mit Direkt-Link als erstem Tab KEIN aria-selected haben (sonst null → Close).
      const fallbackTab =
        section.querySelector('[role="tab"][aria-selected="true"]') ||
        section.querySelector('[role="tab"]');
      if (btnRight && !showRight && btnRight === document.activeElement) {
        (showLeft && btnLeft ? btnLeft : fallbackTab)?.focus({ preventScroll: true });
      }
      if (btnLeft && !showLeft && btnLeft === document.activeElement) {
        (showRight && btnRight ? btnRight : fallbackTab)?.focus({ preventScroll: true });
      }

      if (btnRight) btnRight.hidden = !showRight;
      if (btnLeft) btnLeft.hidden = !showLeft;

      // mask-image-basierter Fade — kein DOM-Overlap
      if (showLeft && showRight) tabsContainer.dataset.scrollFade = 'both';
      else if (showLeft) tabsContainer.dataset.scrollFade = 'left';
      else if (showRight) tabsContainer.dataset.scrollFade = 'right';
      else delete tabsContainer.dataset.scrollFade;
    };

    this.#resizeObserver = new ResizeObserver(update);
    this.#resizeObserver.observe(tabsContainer);
    tabsContainer.addEventListener('scroll', update);
    update();

    // Drag-to-scroll — nur einmal pro Container initialisieren
    if (!tabsContainer.dataset.dragInit) {
      tabsContainer.dataset.dragInit = 'true';
      this.#initDragScroll(tabsContainer);
    }
  }

  /** Maus-Drag-Scrolling für den Tab-Container. */
  #initDragScroll(container) {
    container.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const startX = e.pageX;
      const scrollStart = container.scrollLeft;
      container.classList.add('is-dragging');
      e.preventDefault();

      const onMove = (e) => {
        container.scrollLeft = scrollStart - (e.pageX - startX);
      };
      const onUp = () => {
        container.classList.remove('is-dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
  }
}

customElements.define('hali-mega-menu', HaliMegaMenu);
